<template>
  <el-dialog
    class="create-revision-dialog"
    visible
    :close-on-click-modal="false"
    :show-close="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="modalTitle"
    />
    <el-row :gutter="20">
      <el-col :span="24">
        <el-steps
          :active="stepActive"
          finish-status="success"
          :align-center="true"
        >
          <el-step
            :title="$t('analysesStatusManagement.revision.step1')"
          ></el-step>
          <el-step
            :title="$t('analysesStatusManagement.revision.step2')"
          ></el-step>
          <el-step
            :title="$t('analysesStatusManagement.revision.step3')"
          ></el-step>
        </el-steps>
      </el-col>
    </el-row>
    <el-divider />
    <ValidationObserver v-slot="{ handleSubmit, invalid }">
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          v-if="stepActive === 0"
          :gutter="20"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('analysesStatusManagement.revision.whodunnit')"
            >
              <el-radio-group v-model="formData.whodunnit">
                <el-radio label="client">
                  {{ $t('analysesStatusManagement.revision.customerError') }}
                </el-radio>
                <el-radio label="own">
                  {{ $t('analysesStatusManagement.revision.ownError') }}
                </el-radio>
              </el-radio-group>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          v-if="stepActive === 0"
          :gutter="20"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('analysesStatusManagement.revision.errorNotes')"
            >
              <el-input
                v-model="formData.errorNotes"
                type="textarea"
              />
            </FormItem>
          </el-col>
        </el-row>

        <EditSampleForm
          v-if="stepActive === 1"
          :initialData="formData.updatedSample"
          showSamplingInfo
          @update="sampleDataUpdated"
        />

        <el-collapse
          v-if="stepActive === 2"
          accordion
          v-model="accordionActiveItem"
        >
          <div
            v-for="(analysisValue, index) in analysisList"
            :key="index"
          >
            <el-collapse-item :name="index">
              <template slot="title">
                {{ analysisValue.code }} - {{ analysisValue.description }}
              </template>
              <div
                v-for="operation in analysisValue.operationsWithValues"
                :key="
                  operation.operationRegistryId +
                  '' +
                  operation.labSampleAnalysisId
                "
              >
                <div v-if="operation.mainResult">
                  <h2
                    v-if="analysisValue.operationsWithValues.length > 1"
                    class="section-subtitle"
                  >
                    {{ operation.operation }}
                  </h2>
                  <FormOperationValue
                    :operationValue="operation.value"
                    :operation="operation"
                    :sample="sample"
                  />
                </div>
              </div>
            </el-collapse-item>
          </div>
        </el-collapse>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortAction"
          >
            {{ $t('global.cancel') }}
          </el-button>
          <el-button
            type="secondary"
            v-if="stepActive > 0 && stepActive < 3"
            @click.prevent="prevStep"
          >
            {{ $t('global.prev') }}
          </el-button>
          <el-button
            type="primary"
            :disabled="invalid"
            v-if="stepActive < 2"
            @click.prevent="nextStep"
          >
            {{ $t('global.next') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            v-if="stepActive === 2"
            :disabled="invalid"
            @click.prevent="handleSubmit(createRevision)"
          >
            {{ $t('global.confirm') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import pickBy from 'lodash.pickby';
  import analysisStatusManagementHelper from '@/mixins/AnalysisStatusManagementHelper';
  import FormItem from '@/components/FormItem.vue';
  import FormOperationValue from '@/components/FormOperationValue.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import {
    sectorsList,
    sampleDescriptionsList,
    SampleReportType,
  } from '@/config/constants';
  import { samplesApi } from '@/api';
  import moment from 'moment';
  import EditSampleForm from './EditSampleForm.vue';

  const defaultData = {
    whodunnit: null,
    errorNotes: null,
  };

  export default {
    name: 'CreateRevision',
    components: {
      FormItem,
      FormOperationValue,
      ContentHeader,
      FormButtons,
      EditSampleForm,
    },
    props: {
      sample: { type: Object, required: true },
      analysisList: { type: Array, required: true },
      analysisType: {
        type: String,
        required: true,
        validator: (val) => Object.values(SampleReportType).includes(val),
      },
    },
    data() {
      return {
        formData: {
          whodunnit: null,
          errorNotes: null,
          updatedSample: { ...this.sample },
        },
        accordionActiveItem: 0,
        sectorsList,
        sampleDescriptionsList,
        stepActive: 0,
      };
    },
    computed: {
      modalTitle() {
        return `${this.$t('analysesStatusManagement.revision.title')} - ${
          this.sample.labSampleCode
        }`;
      },
    },
    methods: {
      sampleDataUpdated(data) {
        this.formData.updatedSample = data;
      },
      async prepareRevisionData() {
        this.formData.updatedSample =
          await analysisStatusManagementHelper.createAddress(
            this.formData.updatedSample,
            this.sample.customerId
          );
        await analysisStatusManagementHelper.updateAddressCountryFields(
          this.formData.updatedSample
        );
        const sampleDataToSend = analysisStatusManagementHelper.serializeSample(
          this.formData.updatedSample
        );
        sampleDataToSend.samplingInfo =
          this.formData.updatedSample.samplingInfo;
        if (
          !this.formData.customerHeaderData &&
          this.formData.addressId === null
        ) {
          sampleDataToSend.addressId = null;
        }
        const analysesValues = this.analysisList
          .map((analysisValue) =>
            analysisValue.operationsWithValues.map((owv) => {
              const {
                measure,
                thermostat,
                temperature,
                operationRegistry,
                ...valueData
              } = owv.value;
              if (valueData.analysisStart) {
                valueData.analysisStart = moment
                  .utc(valueData.analysisStart)
                  .startOf('day')
                  .toDate();
              }
              if (valueData.analysisEnd) {
                valueData.analysisEnd = moment
                  .utc(valueData.analysisEnd)
                  .startOf('day')
                  .toDate();
              }
              return pickBy(valueData, (x) => x !== null);
            })
          )
          .flat(2);
        return {
          errorCause: this.formData.whodunnit,
          errorNotes: this.formData.errorNotes,
          sample: sampleDataToSend,
          analysesValues,
        };
      },
      async createRevision() {
        this.stepActive += 1;
        try {
          const dataToSend = await this.prepareRevisionData();

          switch (this.analysisType) {
            case SampleReportType.Challenge:
              this.$router.push({
                name: 'uploadDocument',
                params: {
                  labSampleId: this.sample.labSampleId,
                  revisionData: dataToSend,
                },
              });
              this.$emit('dialogClose', { success: true });
              break;
            default:
              await samplesApi.createRevisionDefault(
                this.formData.updatedSample.labSampleId,
                dataToSend
              );
              this.$notify({
                showClose: true,
                type: 'success',
                message: this.$t('analysesStatusManagement.revision.success'),
              });
              this.$emit('dialogClose', { success: true });
          }
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.status < 500
                ? error.response.data.error.message
                : this.$t('global.serverError'),
          });
        }
      },
      prevStep() {
        this.stepActive -= 1;
        if (this.stepActive < 0) {
          this.stepActive = 0;
        }
      },
      nextStep() {
        this.stepActive += 1;
        if (this.stepActive > 3) {
          this.stepActive = 3;
        }
      },
      abortAction() {
        this.formData = { ...defaultData };
        this.$emit('dialogClose', { success: false });
      },
    },
  };
</script>
<style lang="scss" scoped>
  .el-switch__label {
    color: inherit;
  }
  .el-switch.is-checked .el-switch__core {
    border-color: $--color-primary;
    background-color: $--color-primary;
  }
</style>
